import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import BanModal from '../components/BanModal';
import SubscriptionModal from '../components/SubscriptionModal';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  min-width: 240px;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
`;

const FilterSelect = styled.select`
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  background: #fff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
`;

const Table = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px 110px 130px 150px;
  gap: 16px;
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const TableRow = styled.div<{ $banned?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 100px 110px 130px 150px;
  gap: 16px;
  padding: 16px 20px;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s;
  background: ${p => p.$banned ? 'rgba(239, 68, 68, 0.03)' : 'transparent'};

  &:hover {
    background: ${p => p.$banned ? 'rgba(239, 68, 68, 0.06)' : '#f8fafc'};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const PremiumBadge = styled.span<{ $active?: boolean }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => p.$active ? 'rgba(45, 188, 175, 0.1)' : 'rgba(100, 116, 139, 0.1)'};
  color: ${p => p.$active ? '#2DBCAF' : '#64748b'};
`;

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BannedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`;

const RoleBadge = styled.span<{ $role: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${p => 
    p.$role === 'SUPERADMIN' ? 'rgba(239, 68, 68, 0.1)' :
    p.$role === 'ADMIN' ? 'rgba(59, 130, 246, 0.1)' :
    'rgba(100, 116, 139, 0.1)'
  };
  color: ${p => 
    p.$role === 'SUPERADMIN' ? '#dc2626' :
    p.$role === 'ADMIN' ? '#2563eb' :
    '#64748b'
  };
`;

const RoleSelect = styled.select<{ $role: string }>`
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid ${p => 
    p.$role === 'SUPERADMIN' ? '#fecaca' :
    p.$role === 'ADMIN' ? '#bfdbfe' :
    '#e2e8f0'
  };
  background: ${p => 
    p.$role === 'SUPERADMIN' ? 'rgba(239, 68, 68, 0.1)' :
    p.$role === 'ADMIN' ? 'rgba(59, 130, 246, 0.1)' :
    '#fff'
  };
  color: ${p => 
    p.$role === 'SUPERADMIN' ? '#dc2626' :
    p.$role === 'ADMIN' ? '#2563eb' :
    '#64748b'
  };
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ActionBtns = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionBtn = styled.button<{ $variant?: 'danger' | 'success' | 'default' }>`
  padding: 6px 12px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  
  ${p => p.$variant === 'danger' ? `
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
    &:hover { background: rgba(239, 68, 68, 0.2); }
  ` : p.$variant === 'success' ? `
    background: rgba(34, 197, 94, 0.1);
    color: #16a34a;
    &:hover { background: rgba(34, 197, 94, 0.2); }
  ` : `
    background: rgba(100, 116, 139, 0.1);
    color: #64748b;
    &:hover { background: rgba(100, 116, 139, 0.2); }
  `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-wrap: wrap;
  gap: 12px;
`;

const PaginationInfo = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const PaginationBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  padding: 8px 14px;
  border: 1px solid ${p => p.$active ? '#2DBCAF' : '#e2e8f0'};
  border-radius: 8px;
  background: ${p => p.$active ? '#2DBCAF' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#64748b'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #2DBCAF;
    color: ${p => p.$active ? '#fff' : '#2DBCAF'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

interface User {
  _id: string;
  name: string;
  email: string;
  avatar: string;
  role: string;
  createdAt: string;
  isBanned?: boolean;
  banReason?: string;
  bannedAt?: string;
  subscription?: {
    type?: 'monthly' | 'yearly';
    status?: 'active' | 'expired';
    startDate?: string;
    endDate?: string;
  };
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface BanModalState {
  type: 'ban' | 'unban';
  user: User;
}

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [banModal, setBanModal] = useState<BanModalState | null>(null);
  const [subscriptionUser, setSubscriptionUser] = useState<User | null>(null);
  
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';
  const isAdmin = currentUser?.role === 'ADMIN' || isSuperAdmin;

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);

      const res = await fetch(`/api/admin/users?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchUsers(1), 300);
    return () => clearTimeout(debounce);
  }, [fetchUsers]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });
      
      if (res.ok) {
        setUsers(users.map(u => u._id === userId ? { ...u, role: newRole } : u));
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to update role');
      }
    } catch (err) {
      console.error('Failed to update role:', err);
      alert('Failed to update role');
    }
  };

  const handleDelete = async (userId: string, userName: string) => {
    if (!confirm(`Hapus user "${userName}"? Semua data termasuk skor akan dihapus.`)) return;

    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        setUsers(users.filter(u => u._id !== userId));
        setPagination(p => ({ ...p, total: p.total - 1 }));
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Failed to delete user');
    }
  };

  const handleBan = async (userId: string, reason: string) => {
    const res = await fetch(`/api/admin/users/${userId}/ban`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reason }),
    });
    
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || 'Failed to ban user');
    }
    
    // Update local state
    setUsers(users.map(u => u._id === userId ? { ...u, isBanned: true, banReason: reason } : u));
  };

  const handleUnban = async (userId: string, reason: string) => {
    const res = await fetch(`/api/admin/users/${userId}/unban`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reason }),
    });
    
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || 'Failed to unban user');
    }
    
    // Update local state
    setUsers(users.map(u => u._id === userId ? { ...u, isBanned: false } : u));
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Check if current admin can ban target user
  const canBanUser = (targetUser: User) => {
    // Cannot ban primary superadmin
    if (targetUser.email === 'kindergame.id@gmail.com') return false;
    // Cannot ban yourself
    if (targetUser._id === currentUser?._id) return false;
    // ADMIN cannot ban other ADMIN or SUPERADMIN
    if (currentUser?.role === 'ADMIN' && (targetUser.role === 'ADMIN' || targetUser.role === 'SUPERADMIN')) {
      return false;
    }
    return true;
  };

  return (
    <>
      <PageHeader>
        <Title>Pengguna</Title>
        <SearchBar>
          <SearchInput
            type="text"
            placeholder="Cari nama atau email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FilterSelect value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">Semua Role</option>
            <option value="ENDUSER">End User</option>
            <option value="ADMIN">Admin</option>
            <option value="SUPERADMIN">Super Admin</option>
          </FilterSelect>
        </SearchBar>
      </PageHeader>

      <Table>
        <TableHeader>
          <div>User</div>
          <div>Daftar</div>
          <div>Role</div>
          <div>Premium</div>
          <div>Aksi</div>
        </TableHeader>

        {loading ? (
          <LoadingState>Loading...</LoadingState>
        ) : users.length === 0 ? (
          <EmptyState>Tidak ada pengguna ditemukan</EmptyState>
        ) : (
          users.map((user) => {
            const isPrimaryAdmin = user.email === 'kindergame.id@gmail.com';
            const canEditRole = isSuperAdmin && !isPrimaryAdmin;
            const canDelete = isSuperAdmin && !isPrimaryAdmin && user._id !== currentUser?._id;
            const canBan = isAdmin && canBanUser(user);

            return (
              <TableRow key={user._id} $banned={user.isBanned}>
                <UserCell>
                  <Avatar 
                    src={user.avatar || '/assets/images/default-avatar.png'} 
                    alt={user.name || 'User'} 
                  />
                  <UserInfo>
                    <UserName>
                      {user.name || 'No name'}
                      {user.isBanned && <BannedBadge>🚫 Banned</BannedBadge>}
                    </UserName>
                    <UserEmail>{user.email}</UserEmail>
                  </UserInfo>
                </UserCell>

                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  {formatDate(user.createdAt)}
                </div>

                <div>
                  {canEditRole ? (
                    <RoleSelect
                      $role={user.role}
                      value={user.role}
                      onChange={(e) => handleRoleChange(user._id, e.target.value)}
                    >
                      <option value="ENDUSER">End User</option>
                      <option value="ADMIN">Admin</option>
                      <option value="SUPERADMIN">Super Admin</option>
                    </RoleSelect>
                  ) : (
                    <RoleBadge $role={user.role || 'ENDUSER'}>
                      {user.role || 'ENDUSER'}
                    </RoleBadge>
                  )}
                </div>

                <div>
                  <PremiumBadge 
                    $active={user.subscription?.status === 'active'}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSubscriptionUser(user)}
                  >
                    {user.subscription?.status === 'active' 
                      ? `✨ ${user.subscription.type === 'yearly' ? 'Tahunan' : 'Bulanan'}`
                      : '—'
                    }
                  </PremiumBadge>
                </div>

                <ActionBtns>
                  {canBan && (
                    user.isBanned ? (
                      <ActionBtn 
                        $variant="success"
                        onClick={() => setBanModal({ type: 'unban', user })}
                      >
                        ✓ Unban
                      </ActionBtn>
                    ) : (
                      <ActionBtn 
                        $variant="danger"
                        onClick={() => setBanModal({ type: 'ban', user })}
                      >
                        🚫 Ban
                      </ActionBtn>
                    )
                  )}
                  {canDelete && (
                    <ActionBtn 
                      $variant="danger"
                      onClick={() => handleDelete(user._id, user.name || user.email)}
                    >
                      Hapus
                    </ActionBtn>
                  )}
                </ActionBtns>
              </TableRow>
            );
          })
        )}

        {pagination.pages > 1 && (
          <Pagination>
            <PaginationInfo>
              Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </PaginationInfo>
            <PaginationBtns>
              <PageBtn 
                onClick={() => fetchUsers(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                ← Prev
              </PageBtn>
              <PageBtn 
                onClick={() => fetchUsers(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
              >
                Next →
              </PageBtn>
            </PaginationBtns>
          </Pagination>
        )}
      </Table>

      {/* Ban/Unban Modal */}
      {banModal && (
        <BanModal
          type={banModal.type}
          user={banModal.user}
          onClose={() => setBanModal(null)}
          onConfirm={async (reason) => {
            if (banModal.type === 'ban') {
              await handleBan(banModal.user._id, reason);
            } else {
              await handleUnban(banModal.user._id, reason);
            }
          }}
        />
      )}

      {/* Subscription Modal */}
      {subscriptionUser && (
        <SubscriptionModal
          user={subscriptionUser}
          onClose={() => setSubscriptionUser(null)}
          onSave={() => fetchUsers(pagination.page)}
        />
      )}
    </>
  );
};

export default UsersPage;
